import { useMemo } from 'react';
import type { IFabricObservabilityRunEnvelope } from '@skystone/shared';
import type { ColumnDef } from '@/components/ui';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  ErrorBanner,
  PageHeader,
  PageLoading,
} from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { fabricSyncApi } from '@/services/fabric-sync-api';
import { PageContainer } from '@/components/layout';
import {
  createTextColumn,
  DependencyGateDetailsCard,
  formatOptionalPrimitive,
  formatScopeDescriptor,
  TEXT_FILTER,
  SyncSummaryCard,
} from './fabric-sync-status-page-shared';

const runColumns: ColumnDef<IFabricObservabilityRunEnvelope>[] = [
  createTextColumn<IFabricObservabilityRunEnvelope>('runType', 'Run Type', 'runType'),
  createTextColumn<IFabricObservabilityRunEnvelope>('runStatus', 'Run Status', 'runStatus'),
  createTextColumn<IFabricObservabilityRunEnvelope>('scope', 'Scope', 'scope'),
  createTextColumn<IFabricObservabilityRunEnvelope>('runId', 'Run ID', 'runId', (value) =>
    formatOptionalPrimitive(value),
  ),
  createTextColumn<IFabricObservabilityRunEnvelope>('startedAt', 'Started At', 'startedAt', (value) =>
    formatOptionalPrimitive(value),
  ),
  createTextColumn<IFabricObservabilityRunEnvelope>('finishedAt', 'Finished At', 'finishedAt', (value) =>
    formatOptionalPrimitive(value),
  ),
  createTextColumn<IFabricObservabilityRunEnvelope>('asOf', 'As Of', 'asOf', (value) =>
    formatOptionalPrimitive(value),
  ),
  {
    id: 'scopeDescriptor',
    header: 'Scope Descriptor',
    accessor: (row) => formatScopeDescriptor(row.scopeDescriptor),
    sortable: true,
    filter: TEXT_FILTER,
  },
];

export function TblRawSyncStatusPage() {
  const { data, loading, error } = useApi(() => fabricSyncApi.getRawSyncStatus(), []);

  const runs = useMemo(() => data?.runs ?? [], [data]);
  const dependencies = useMemo(() => data?.dependencyGate.dependencies ?? [], [data]);

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Tbl - sync_status_raw"
        subtitle="Raw-layer sync observability with dependency gate context."
      />

      <ErrorBanner message={error} />

      {data && (
        <SyncSummaryCard
          title="Raw Sync Summary"
          sourceLayer={data.sourceLayer}
          asOf={data.asOf}
          dependencyGateStatus={data.dependencyGate.status}
          reasonCodes={data.reasonCodes}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Run Envelopes</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={runs}
            columns={runColumns}
            keyExtractor={(run) => `${run.runType}:${run.scope}:${run.runId ?? 'missing-run-id'}`}
            enablePagination
            enableFilters
            emptyMessage="No raw run envelopes found."
          />
        </CardContent>
      </Card>

      <DependencyGateDetailsCard dependencies={dependencies} emptyMessage="No raw dependencies found." />
    </PageContainer>
  );
}
